/**
 * What is running, where every page can see it: the live pill and its tray in the top bar, the
 * toasts when something lands, and the live line beside a page title (2026-10-06).
 *
 * 🔴 EVERY WORD HERE IS THE FEED'S, AND THE FEED IS THE DATABASE'S. A stage is the attempt's own
 * (`domain/live.py`), never the queue's `dispatched`, which stays after a run has ended.
 * ⚠️ A TOAST IS NEVER THE ONLY PLACE A FACT APPEARS. Everything one says is also in the tray's
 * last hour, so a toast that faded while somebody looked away loses nothing.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import type { LiveFinished, LiveGrouped, LiveOther, LiveQueued, LiveRun } from "./api";
import {
  clock, Elapsed, place, since, Spin, STAGE_WORDS, useLive, useNow, type LiveEvent,
} from "./live";
import { attentionHref, routes } from "./routes";

const who = (name: string) => name || "a submission";
const titled = (name: string, title: string) => `${who(name)}${title ? ` — ${title}` : ""}`;

/** "88/100 · needs your review" — what a stored grade says on a toast and in the tray. */
function gradedLine(f: LiveFinished): string {
  if (f.points == null) return "nothing could be measured, so no mark was given";
  const mark = `${f.points}/${f.max_points ?? "?"}`;
  if (f.status === "REVIEW") return `${mark} · needs your review`;
  if (f.status === "READY") return `${mark} · ready`;
  return mark;
}

/** What one ending is called, in one place for the tray and the toasts. */
export function ending(f: LiveFinished): { word: string; line: string; ok: boolean } {
  switch (f.outcome) {
    case "recorded": return { word: "Graded", line: gradedLine(f), ok: true };
    case "held": return {
      word: "Held",
      line: `${f.detail ? `${f.detail} ` : ""}Tried again${
        f.retry_finalise_at ? ` at ${clock(f.retry_finalise_at)}` : " shortly"}, from the run already made.`,
      ok: false };
    case "limited": return {
      word: "Paused", line: f.detail || "The subscription's limit stopped this run.", ok: false };
    case "refused": return { word: "Refused", line: f.detail || "It was refused.", ok: false };
    default: return { word: "Stored nothing", line: f.detail || "It ended without a grade.",
                      ok: false };
  }
}

/** Where an ending sends somebody who wants to know more. A report opens over the Assignments
 *  page, keeping that page's own address when the reader is on it (`routes.report`). */
function endingHref(courseId: string, f: LiveFinished,
                    here: { pathname: string; search: string }): { to: string; label: string } {
  return f.outcome === "recorded"
    ? { to: routes.report(courseId, f.submission_id, here), label: "Open report" }
    : { to: routes.page(courseId, "runs"), label: "Open in Runs" };
}

/** ⚠️ NAMED, NOT ADDRESSED: whoever pressed Run is a teacher, and "you" is the one reading. */
function by(requestedBy: string, me: string): string {
  if (!requestedBy) return "";
  return requestedBy.toLowerCase() === me.toLowerCase() ? "you" : requestedBy.split("@")[0]!;
}

// ── the pill and its tray ──────────────────────────────────────────────────────────────────

export function LivePill({ courseId, me }: { courseId: string; me: string }) {
  const live = useLive();
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const f = live.feed;
  const n = f?.counts ?? { grading: 0, queued: 0, other: 0 };
  const parts = [
    n.grading ? `${n.grading} grading` : "",
    n.queued ? `${n.queued} queued` : "",
    n.other ? `${n.other} other` : "",
  ].filter(Boolean);
  const failing = live.failures > 0;
  const label = failing ? "Offline" : parts.length ? parts.join(" · ") : "Live";

  // The tray asks at the fast cadence while it is open: somebody is watching it.
  useEffect(() => (open ? live.watch() : undefined), [open, live]);

  return (
    <>
      <button ref={btn} type="button" aria-haspopup="dialog" aria-expanded={open}
              className={`live-pill${failing ? " warn" : ""}${parts.length ? " busy" : ""}`}
              aria-label={`What is running: ${label}`}
              onClick={() => setOpen((o) => !o)}>
        {failing ? <span className="live-bang" aria-hidden="true">!</span>
          : parts.length ? <Spin /> : <span className="live-dot" aria-hidden="true" />}
        <span>{label}</span>
      </button>
      {open && <LiveTray courseId={courseId} me={me} anchor={btn}
                         onClose={() => { setOpen(false); btn.current?.focus(); }} />}
    </>
  );
}

/**
 * The console-wide answer to "is anything happening?": what is grading, what is waiting, other
 * work, and the last hour.
 *
 * ⚠️ `fixed`, PLACED FROM THE PILL'S RECTANGLE — the `.why-pop` answer — so the top bar cannot
 * clip it. It closes on Escape, on a click outside and on the pill, and focus goes back to the
 * pill, because a dialog with one way out is a trap.
 * 🔴 PORTALLED TO THE BODY, BECAUSE THE BAND IS `color-scheme: dark`. [X] Rendered in light mode
 * (2026-10-06): inside the band every themed token resolved to its dark half, so the tray — a
 * list of students and marks, which is page content — stayed dark over a light page.
 * 🔴 AND KEPT INSIDE THE SCREEN. [X] At 390px the pill wraps to the left of the band, and a tray
 * hung from its right edge ran off the left of the phone.
 */
export function LiveTray({ courseId, me, anchor, onClose }: {
  courseId: string; me: string; anchor: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}) {
  const live = useLive();
  const box = useRef<HTMLElement>(null);
  const [at, setAt] = useState<{ top: number; right: number }>({ top: 64, right: 16 });

  useLayoutEffect(() => {
    const r = anchor.current?.getBoundingClientRect();
    if (r) {
      const width = Math.min(480, window.innerWidth - 24);
      // Hung from the pill's right edge, but never so far left that it leaves the screen.
      const right = Math.min(Math.max(12, window.innerWidth - r.right),
                             Math.max(12, window.innerWidth - 12 - width));
      setAt({ top: r.bottom + 8, right });
    }
    box.current?.focus();
  }, [anchor]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); onClose(); } };
    const down = (e: MouseEvent) => {
      const t = e.target as Node;
      if (box.current?.contains(t) || anchor.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("keydown", key);
    document.addEventListener("mousedown", down);
    return () => {
      document.removeEventListener("keydown", key);
      document.removeEventListener("mousedown", down);
    };
  }, [onClose, anchor]);

  const f = live.feed;
  const runs = f?.runs ?? [];
  const queued = f?.queued ?? [];
  const other = f?.other ?? [];
  const ended = f?.finished ?? [];
  const grouped = f?.grouped ?? [];
  return createPortal(
    <section ref={box} className="live-tray" role="dialog" aria-label="What is running"
             tabIndex={-1} style={{ top: at.top, right: at.right }}>
      <header className="live-tray-head">
        <h2 className="live-tray-title">What is running</h2>
        <button type="button" className="btn ghost sm" aria-label="Close" onClick={onClose}>×</button>
      </header>
      {!f && !live.error && <p className="live-tray-empty">Asking…</p>}
      {f && runs.length + queued.length + other.length === 0 && (
        <p className="live-tray-empty">Nothing is running or waiting in this cohort.</p>)}

      {runs.length > 0 && (
        <TraySection label="Grading" n={runs.length}>
          {runs.map((r) => <RunRow key={r.attempt_id} courseId={courseId} r={r} me={me} />)}
        </TraySection>)}
      {queued.length > 0 && (
        <TraySection label="Queued" n={queued.length} note="starts as soon as a grader is free">
          {queued.map((q) => <QueuedRow key={q.submission_id} courseId={courseId} q={q} me={me} />)}
        </TraySection>)}
      {other.length > 0 && (
        <TraySection label="Other work" n={other.length}>
          {other.map((o, i) => <OtherRow key={i} courseId={courseId} o={o} />)}
        </TraySection>)}
      {ended.length + grouped.length > 0 && (
        <TraySection label="Finished in the last hour" n={ended.length + grouped.length}>
          {ended.map((e) => <EndedRow key={`${e.attempt_id}-${e.outcome}`} courseId={courseId}
                                      e={e} />)}
          {grouped.map((g) => <GroupedRow key={g.id} courseId={courseId} g={g} />)}
        </TraySection>)}

      <footer className="live-tray-foot">
        <LiveLine />
        {f?.classroom_checked_at && (
          <span className="live-tray-checked">Classroom checked <Ago iso={f.classroom_checked_at} /></span>)}
        <span className="spacer" />
        <Link to={routes.page(courseId, "runs")} onClick={onClose}>All runs →</Link>
      </footer>
    </section>,
    document.body);
}

function TraySection({ label, n, note, children }: {
  label: string; n: number; note?: string; children: React.ReactNode;
}) {
  return (
    <div className="live-sec">
      <div className="live-sec-lab">{label} <span className="live-sec-n">{n}</span>
        {note && <span className="live-sec-note">· {note}</span>}</div>
      <ul className="live-rows">{children}</ul>
    </div>
  );
}

function RunRow({ courseId, r, me }: { courseId: string; r: LiveRun; me: string }) {
  const pressed = by(r.requested_by, me);
  const here = useLocation();
  // ⚠️ "Starting…" SAYS WHEN BY ITSELF: it is two seconds old by definition.
  const sub = r.stage === "held"
    ? `tried again${r.retry_finalise_at ? ` at ${clock(r.retry_finalise_at)}` : " shortly"}`
    : r.stage === "starting" ? ""
    : pressed ? `${pressed} started it at ${clock(r.started_at)}`
    : `started at ${clock(r.started_at)}`;
  // A run over a mark opens that mark's report; a first run has no report yet, so it opens the
  // assignment it belongs to.
  const to = r.regrade ? routes.report(courseId, r.submission_id, here)
    : routes.assignment(courseId, r.course_work_id);
  return (
    <li>
      <Link className="live-row" to={to}>
        <Spin dim={r.stage === "starting" || r.stage === "waiting" || r.stage === "held"} />
        <span className="live-who">
          <b>{who(r.student)}</b>{r.assignment_title ? ` — ${r.assignment_title}` : ""}
          {r.regrade && <span className="badge plain live-tag">re-grade</span>}
          <span className="live-sub">{STAGE_WORDS[r.stage]}{sub ? ` · ${sub}` : ""}</span>
        </span>
        {r.stage !== "held" && r.stage !== "starting" && <Elapsed since={since(r)} />}
      </Link>
    </li>
  );
}

function QueuedRow({ courseId, q, me }: { courseId: string; q: LiveQueued; me: string }) {
  const pressed = by(q.requested_by, me);
  const sub = q.requested
    ? `${pressed === "you" ? "You" : pressed || "A teacher"} pressed Run at ${clock(q.since)}`
    : `waiting since ${clock(q.since)} · unattended grading`;
  return (
    <li>
      <Link className="live-row" to={routes.assignment(courseId, q.course_work_id)}>
        <span className="ring" aria-hidden="true" />
        <span className="live-who">
          <b>{who(q.student)}</b>{q.assignment_title ? ` — ${q.assignment_title}` : ""}
          <span className="live-sub">{sub}</span>
        </span>
        <span className="live-t live-tq">{place(q.position)}</span>
      </Link>
    </li>
  );
}

function OtherRow({ courseId, o }: { courseId: string; o: LiveOther }) {
  if (o.kind === "team_grouping") {
    return (
      <li>
        <Link className="live-row" to={routes.teams(courseId, "teams")}>
          <Spin dim={o.status === "queued"} />
          <span className="live-who">
            <b>Team grouping</b> · {o.eligible} answer{o.eligible === 1 ? "" : "s"}
            <span className="live-sub">{o.status === "queued" ? "waiting to start"
                                                               : "reading the answers"}</span>
          </span>
          <Elapsed since={o.started_at ?? o.requested_at} />
        </Link>
      </li>);
  }
  return (
    <li>
      <Link className="live-row" to={routes.page(courseId, "social")}>
        <Spin />
        <span className="live-who">
          <b>Social activity</b>
          <span className="live-sub">{o.job_kind === "resolve" ? "looking up a handle"
                                                               : "collecting the week's posts"}</span>
        </span>
        <Elapsed since={o.started_at} />
      </Link>
    </li>);
}

/** What a team grouping's ending is called, and the line under it. */
function grouping(g: LiveGrouped): { word: string; line: string; ok: boolean } {
  return g.outcome === "ready"
    ? { word: "Teams proposed", ok: true,
        line: `from ${g.eligible} answer${g.eligible === 1 ? "" : "s"} · ready to review` }
    : { word: "Team grouping failed", ok: false, line: g.detail || "It made no plan." };
}

function GroupedRow({ courseId, g }: { courseId: string; g: LiveGrouped }) {
  const { word, line, ok } = grouping(g);
  return (
    <li>
      <Link className="live-row" to={routes.teams(courseId, "teams")}>
        <span className={ok ? "live-ok" : "live-warn"} aria-hidden="true">{ok ? "✓" : "!"}</span>
        <span className="live-who">
          <b>{word}</b>
          <span className="live-sub">{line}</span>
        </span>
        <span className="live-t live-tq"><Ago iso={g.finished_at} /></span>
      </Link>
    </li>);
}

function EndedRow({ courseId, e }: { courseId: string; e: LiveFinished }) {
  const { word, line, ok } = ending(e);
  const go = endingHref(courseId, e, useLocation());
  return (
    <li>
      <Link className="live-row" to={go.to}>
        <span className={ok ? "live-ok" : "live-warn"} aria-hidden="true">{ok ? "✓" : "!"}</span>
        <span className="live-who">
          <b>{who(e.student)}</b>{e.assignment_title ? ` — ${e.assignment_title}` : ""}
          <span className="live-sub">{word} · {line}</span>
        </span>
        <span className="live-t live-tq"><Ago iso={e.finished_at} /></span>
      </Link>
    </li>);
}

/** "2 min ago", ticking. */
function Ago({ iso }: { iso: string | null | undefined }) {
  const now = useNow();
  const { skew } = useLive();
  if (!iso) return null;
  const s = Math.max(0, Math.floor((now + skew - Date.parse(iso)) / 1000));
  const text = s < 45 ? "just now" : s < 3600 ? `${Math.round(s / 60)} min ago`
    : `${Math.floor(s / 3600)} h ago`;
  return <span title={iso}>{text}</span>;
}

// ── the live line ──────────────────────────────────────────────────────────────────────────

/**
 * Beside a page title: whether what the page shows is current.
 *
 * ⚠️ FIVE STATES, AND "UPDATING" ONLY AFTER A SECOND — a poll answers in tens of milliseconds, and
 * a word that flickers every three seconds is noise somebody learns to ignore.
 */
export function LiveLine() {
  const live = useLive();
  const now = useNow();
  if (live.hidden) {
    return <span className="live-line"><span className="ring" aria-hidden="true" />
      Paused while this tab is hidden</span>;
  }
  if (live.failures > 0) {
    const wait = live.retryAt ? Math.max(0, Math.ceil((live.retryAt - now) / 1000)) : null;
    return (
      <span className="live-line warn" role="status">
        <span className="live-bang" aria-hidden="true">!</span>
        Can&rsquo;t reach the console{wait != null ? ` · retrying in ${wait} s` : ""}
        <button type="button" className="btn sm" onClick={live.poke}>Retry now</button>
      </span>);
  }
  if (!live.at) {
    return <span className="live-line"><Spin dim /> Connecting…</span>;
  }
  const s = Math.max(0, Math.floor((now - live.at) / 1000));
  if (live.polling && s >= 1) {
    return <span className="live-line"><Spin dim /> Updating…</span>;
  }
  return (
    <span className="live-line"><span className="live-dot" aria-hidden="true" />
      Live · updated {s < 2 ? "just now" : `${s} s ago`}</span>);
}

// ── toasts ─────────────────────────────────────────────────────────────────────────────────

/** How long a toast stays, unless it is being read. */
export const TOAST_MS = 10_000;
/** More endings than this in one answer are told as one toast. */
export const COLLAPSE_OVER = 3;

/** One toast: a run's ending (or several, collapsed), or a team grouping's. */
type Toast = { id: number; at: number; items: LiveFinished[]; grouped?: LiveGrouped };

/**
 * One toast per ending the page had not seen, bottom right.
 *
 * ⚠️ MORE THAN THREE IN ONE ANSWER ARE ONE TOAST — a tab that was hidden for an hour comes back to
 * a sentence, not a stack. ⚠️ IT STAYS WHILE IT IS BEING READ: hovering or focusing the region
 * holds every toast, and leaving starts the clocks again from where they were.
 * ⚠️ `role="status"`, POLITE — an ending is news, not an emergency, and an assertive region would
 * interrupt a screen reader mid-sentence for every run that lands.
 */
export function Toasts({ courseId }: { courseId: string }) {
  const live = useLive();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seen = useRef(0);
  const [held, setHeld] = useState(false);
  const heldSince = useRef<number | null>(null);
  const now = useNow();

  useEffect(() => {
    const fresh = live.events.filter((e: LiveEvent) => e.n > seen.current);
    // 🔴 A GROUPING THAT ENDED IS NEWS TOO, and it is never collapsed into the runs: "Teams
    // proposed" is a different act from "graded", with a different page to open.
    const groups = live.grouped.filter((e) => e.n > seen.current);
    if (!fresh.length && !groups.length) return;
    seen.current = Math.max(...fresh.map((e) => e.n), ...groups.map((e) => e.n));
    const at = Date.now();
    setToasts((ts) => {
      const next: Toast[] = fresh.length > COLLAPSE_OVER
        ? [{ id: fresh[0]!.n, at, items: fresh.map((e) => e.item) }]
        : fresh.map((e) => ({ id: e.n, at, items: [e.item] }));
      for (const g of groups) next.push({ id: g.n, at, items: [], grouped: g.item });
      return [...ts, ...next].slice(-4);
    });
  }, [live.events, live.grouped]);

  // Expire what has been up long enough — never while somebody is reading.
  useEffect(() => {
    if (held) return;
    setToasts((ts) => {
      const keep = ts.filter((t) => now - t.at < TOAST_MS);
      return keep.length === ts.length ? ts : keep;
    });
  }, [now, held]);

  const hold = (on: boolean) => {
    if (on) { heldSince.current = Date.now(); setHeld(true); return; }
    const since = heldSince.current;
    heldSince.current = null;
    setHeld(false);
    // The time it was held does not count against it.
    if (since != null) {
      const paused = Date.now() - since;
      setToasts((ts) => ts.map((t) => ({ ...t, at: t.at + paused })));
    }
  };

  return (
    <div className="toasts" role="status" aria-live="polite"
         onMouseEnter={() => hold(true)} onMouseLeave={() => hold(false)}
         onFocus={() => hold(true)} onBlur={() => hold(false)}>
      {toasts.map((t) => (
        <ToastView key={t.id} courseId={courseId} t={t}
                   onClose={() => setToasts((ts) => ts.filter((x) => x.id !== t.id))} />))}
    </div>
  );
}

function ToastView({ courseId, t, onClose }: {
  courseId: string; t: Toast; onClose: () => void;
}) {
  // Read before the early return: a hook is called on every render or on none.
  const here = useLocation();
  if (t.grouped) {
    const { word, line, ok } = grouping(t.grouped);
    return (
      <div className={ok ? "toast" : "toast warn"}>
        <span className={ok ? "live-ok" : "live-warn"} aria-hidden="true">{ok ? "✓" : "!"}</span>
        <div className="toast-body">
          <div className="toast-t"><b>{word}</b></div>
          <div className="toast-s">{line}</div>
          <div className="toast-a">
            <Link className="btn sm" to={routes.teams(courseId, "teams")} onClick={onClose}>
              Open Teams</Link>
          </div>
        </div>
        <button type="button" className="toast-x" aria-label="Dismiss" onClick={onClose}>×</button>
      </div>);
  }
  if (t.items.length > 1) {
    const graded = t.items.filter((x) => x.outcome === "recorded");
    const review = graded.filter((x) => x.status === "REVIEW").length;
    const other = t.items.length - graded.length;
    return (
      <div className="toast">
        <span className="live-ok" aria-hidden="true">✓</span>
        <div className="toast-body">
          <div className="toast-t"><b>{t.items.length} runs ended</b> while you were away</div>
          <div className="toast-s">
            {graded.length} graded{review ? ` · ${review} need your review` : ""}
            {other ? ` · ${other} ended without a grade` : ""}</div>
          <div className="toast-a">
            {review > 0
              ? <Link className="btn sm" onClick={onClose}
                      to={attentionHref(courseId, { page: "assignments", tab: "review" })}>
                  Show In review</Link>
              : <Link className="btn sm" onClick={onClose} to={routes.page(courseId, "runs")}>
                  Open Runs</Link>}
          </div>
        </div>
        <button type="button" className="toast-x" aria-label="Dismiss" onClick={onClose}>×</button>
      </div>);
  }
  const e = t.items[0]!;
  const { word, line, ok } = ending(e);
  const go = endingHref(courseId, e, here);
  return (
    <div className={ok ? "toast" : "toast warn"}>
      <span className={ok ? "live-ok" : "live-warn"} aria-hidden="true">{ok ? "✓" : "!"}</span>
      <div className="toast-body">
        <div className="toast-t"><b>{word}</b> · {titled(e.student, e.assignment_title)}</div>
        <div className="toast-s">{line}</div>
        <div className="toast-a">
          <Link className="btn sm" to={go.to} onClick={onClose}>{go.label}</Link>
        </div>
      </div>
      <button type="button" className="toast-x" aria-label="Dismiss" onClick={onClose}>×</button>
    </div>);
}
